import { DirectionalIcon, FormField, isolate } from '@batiflow/ui';
import {
  Button,
  Checkbox,
  Radio,
  RadioGroup,
  Spinner,
  __css,
  tokens,
} from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronRight16Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScopeItem } from '@batiflow/contracts';
import { useFolderChildren } from '@/features/documents/api';
import { useSpaceChannels } from './api';
import { EMPTY_LIMITED_DRAFT, WHOLE_SCOPE_DRAFT, toggled, type ScopeDraft } from './model';

const WHOLE = 'all';
const SOME = 'some';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"group":{"Bokbzmb":0,"Jyy4pa":0,"B1u1w3b":"f1r0i6kl","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","gggo0z":"fi15kbk","Behc1zw":"f10zy6vx","B4m11q7":"f32hy0g","tzxy30":"fgmlvap","B834pou":"f1taw3pw","uk9mye":"f1p2dij1","rm1204":"fwyddsq","Bwezv66":"fwl1lcr","F1poyb":"foi3q75","mbw9mh":"f1a566p","ijz38e":"f1ftuoky","Ctt3hk":"f1crbv9w","Bgto7am":"fuzgk9b"},"legend":{"Bhrd7zp":"fl43uef","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1rktw8j"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62"},"nested":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f12g30ep","Bnt446c":"fnmerfl","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62"},"row":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1ufnopg"},"spacer":{"Bns2gvi":"f1shmqcm","Bnnss6s":"fi64zpg"},"muted":{"sj55zd":"fkfq4zb","Be2twd7":"fkhj508"},"error":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fy9rknc","sj55zd":"f1hcrxcs"},"hint":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"}});

interface FoldersProps {
  spaceId: string;
  selected: readonly string[];
  onToggle: (folderId: string, on: boolean) => void;
}

/** One folder of the tree: its checkbox, and its children once it is opened. */
function FolderNode({
  spaceId,
  folder,
  selected,
  onToggle,
}: FoldersProps & { folder: { id: string; name: string; hasChildren: boolean } }) {
  const { t } = useTranslation('spaces');
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  return (
    <li>
      <div className={styles.row}>
        {folder.hasChildren ? (
          <Button
            appearance="subtle"
            size="small"
            aria-expanded={open}
            aria-label={t(open ? 'sharing.scope.collapse' : 'sharing.scope.expand', {
              name: isolate(folder.name),
            })}
            icon={
              open ? <ChevronDown16Regular /> : <DirectionalIcon icon={ChevronRight16Regular} />
            }
            onClick={() => {
              setOpen((value) => !value);
            }}
          />
        ) : (
          <span className={styles.spacer} aria-hidden="true" />
        )}
        <Checkbox
          checked={selected.includes(folder.id)}
          label={<bdi>{folder.name}</bdi>}
          onChange={(_event, data) => {
            onToggle(folder.id, data.checked === true);
          }}
        />
      </div>
      {open ? (
        <FolderChildren
          spaceId={spaceId}
          parentId={folder.id}
          selected={selected}
          onToggle={onToggle}
        />
      ) : null}
    </li>
  );
}

/** The readable children of a folder (the root folders when `parentId` is null), loaded on demand. */
function FolderChildren({
  spaceId,
  parentId,
  selected,
  onToggle,
}: FoldersProps & { parentId: string | null }) {
  const { t } = useTranslation('spaces');
  const styles = useStyles();
  const children = useFolderChildren(spaceId, parentId);
  if (children.isError) return <p className={styles.muted}>{t('sharing.scope.foldersFailed')}</p>;
  if (!children.data) return <Spinner size="tiny" label={t('sharing.scope.loading')} />;
  if (children.data.items.length === 0) {
    return parentId === null ? (
      <p className={styles.muted}>{t('sharing.scope.noFolders')}</p>
    ) : null;
  }
  return (
    <ul className={parentId === null ? styles.list : styles.nested}>
      {children.data.items.map((folder) => (
        <FolderNode
          key={folder.id}
          spaceId={spaceId}
          folder={folder}
          selected={selected}
          onToggle={onToggle}
        />
      ))}
    </ul>
  );
}

export interface ScopePickerProps {
  spaceId: string;
  value: ScopeDraft;
  onChange: (value: ScopeDraft) => void;
  /** Offers "Tout l'espace" (not for a guest, nor for a delegate of a partial partnership). */
  allowWhole: boolean;
  /**
   * The partnership's own scope when it is limited, named by the server: only these folders and
   * channels can be chosen (a delegate never gets more than the partnership). The partner's admin
   * cannot read the space's folders or channels, so nothing is fetched then.
   */
  within?: readonly ScopeItem[] | null;
  error?: string | null;
}

/**
 * The scope of an access: the whole space, or some folders (with their subfolders; the tree opens
 * folder by folder) and channels of the space.
 */
export function ScopePicker({
  spaceId,
  value,
  onChange,
  allowWhole,
  within,
  error,
}: ScopePickerProps) {
  const { t } = useTranslation('spaces');
  const styles = useStyles();
  const channels = useSpaceChannels(spaceId, !value.whole && !within);
  const limited = !value.whole;
  const channelItems = within
    ? within
        .filter((item) => item.kind === 'channel')
        .map((item) => ({ id: item.id, name: item.name, archivedAt: null }))
    : (channels.data?.items ?? []).filter((channel) => channel.archivedAt === null);
  const withinFolders = (within ?? []).filter((item) => item.kind === 'folder');

  const toggleFolder = (folderId: string, on: boolean) => {
    onChange({ ...value, folderIds: toggled(value.folderIds, folderId, on) });
  };
  const toggleChannel = (channelId: string, on: boolean) => {
    onChange({ ...value, channelIds: toggled(value.channelIds, channelId, on) });
  };

  return (
    <div className={styles.root}>
      {allowWhole ? (
        <FormField label={t('sharing.scope.label')}>
          <RadioGroup
            value={value.whole ? WHOLE : SOME}
            onChange={(_event, data) => {
              onChange(data.value === WHOLE ? WHOLE_SCOPE_DRAFT : EMPTY_LIMITED_DRAFT);
            }}
          >
            <Radio value={WHOLE} label={t('sharing.scope.all')} />
            <Radio value={SOME} label={t('sharing.scope.some')} />
          </RadioGroup>
        </FormField>
      ) : null}
      {limited ? (
        <>
          <fieldset className={styles.group}>
            <legend className={styles.legend}>{t('sharing.scope.foldersLegend')}</legend>
            {within ? (
              withinFolders.length === 0 ? (
                <p className={styles.muted}>{t('sharing.scope.noFolders')}</p>
              ) : (
                <ul className={styles.list}>
                  {withinFolders.map((folder) => (
                    <li key={folder.id}>
                      <Checkbox
                        checked={value.folderIds.includes(folder.id)}
                        label={<bdi>{folder.name}</bdi>}
                        onChange={(_event, data) => {
                          toggleFolder(folder.id, data.checked === true);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <FolderChildren
                spaceId={spaceId}
                parentId={null}
                selected={value.folderIds}
                onToggle={toggleFolder}
              />
            )}
            <p className={styles.hint}>{t('sharing.scope.subfoldersHint')}</p>
          </fieldset>
          <fieldset className={styles.group}>
            <legend className={styles.legend}>{t('sharing.scope.channelsLegend')}</legend>
            {!within && channels.isError ? (
              <p className={styles.muted}>{t('sharing.scope.channelsFailed')}</p>
            ) : !within && !channels.data ? (
              <Spinner size="tiny" label={t('sharing.scope.loading')} />
            ) : channelItems.length === 0 ? (
              <p className={styles.muted}>{t('sharing.scope.noChannels')}</p>
            ) : (
              <ul className={styles.list}>
                {channelItems.map((channel) => (
                  <li key={channel.id}>
                    <Checkbox
                      checked={value.channelIds.includes(channel.id)}
                      label={
                        channel.name ? <bdi>{channel.name}</bdi> : t('sharing.scope.unnamedChannel')
                      }
                      onChange={(_event, data) => {
                        toggleChannel(channel.id, data.checked === true);
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </fieldset>
        </>
      ) : null}
      {error ? (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      ) : null}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/sharing/scope-picker.griffel.css";